/**
 * Tiny notification sounds generated with the Web Audio API — no asset files.
 *
 * Two distinct, soft chimes so staff can tell them apart by ear:
 *  - playArrivalSound(): a gentle two-note rising chime → a client ARRIVED
 *    (the team member hears it: someone is now in their waiting room).
 *  - playCalledSound(): a single soft note → a patient was CALLED
 *    (reception hears it: a call just happened).
 *
 * Browsers block audio until the user interacts with the page, so we lazily
 * create the AudioContext and unlock it on the first pointer/key event.
 */

let ctx: AudioContext | null = null;

function getCtx(): AudioContext | null {
    try {
        if (!ctx) {
            const AC =
                window.AudioContext ||
                (window as unknown as { webkitAudioContext?: typeof AudioContext })
                    .webkitAudioContext;
            if (!AC) return null;
            ctx = new AC();
        }
        if (ctx.state === 'suspended') ctx.resume().catch(() => undefined);
        return ctx;
    } catch {
        return null;
    }
}

/** Unlock audio on the first user gesture (one-time). */
if (typeof window !== 'undefined') {
    const unlock = () => {
        getCtx();
        window.removeEventListener('pointerdown', unlock);
        window.removeEventListener('keydown', unlock);
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
}

/**
 * Play a short sequence of soft sine tones.
 * @param freqs   notes in Hz, played in order
 * @param noteDur seconds per note
 * @param peak    peak gain (kept low so it stays subtle)
 */
function chime(freqs: number[], noteDur = 0.13, peak = 0.05): void {
    const c = getCtx();
    if (!c) return;
    let t = c.currentTime;
    for (const f of freqs) {
        const osc = c.createOscillator();
        const gain = c.createGain();
        osc.type = 'sine';
        osc.frequency.value = f;
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.linearRampToValueAtTime(peak, t + 0.015);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + noteDur);
        osc.connect(gain).connect(c.destination);
        osc.start(t);
        osc.stop(t + noteDur);
        t += noteDur;
    }
}

/** Client arrived in the waiting room — gentle rising three-note chime. */
export function playArrivalSound(): void {
    chime([660, 880, 1175], 0.18, 0.06);
}

/** A patient was called — soft two-note chime (slightly longer). */
export function playCalledSound(): void {
    chime([587, 440], 0.24, 0.055);
}
